import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Fab,
  Grid,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Visibility, CloudUpload, Add, Refresh, Business, Schedule, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { BatchSummary, Schema } from '../services/api';
import SchemaPreviewDialog from './common/SchemaPreviewDialog';
import SchemaSetPickerDialog from './workspace/SchemaSetPickerDialog';
import { useProjectContext } from '../context/ProjectContext';
import { updateProject } from '../services/projectApi';
import { getStatusConfig, formatDate, formatRelativeTime, normalizeStatusForUi } from '../utils/transformationHelpers';
import GuidDisplay from './common/GuidDisplay';
import SmartUploadDialog from './dashboard/SmartUploadDialog';
import type { UploadCompletionResult } from './dashboard/SmartUploadDialog';
import { renameBatch } from '../services/batchApi';

const blinkAnimation = {
  animation: 'pageFlash 350ms ease-in-out',
  '@keyframes pageFlash': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 },
  },
};

const BatchList: React.FC = () => {
  const navigate = useNavigate();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewSchema, setPreviewSchema] = useState<Schema | null>(null);
  const [schemaPickerOpen, setSchemaPickerOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [blinkKey, setBlinkKey] = useState(0);
  const [editingBatchId, setEditingBatchId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const {
    selectedProjectId,
    targetSchema,
    projectSummary,
    isLoadingSummary,
    refreshSummary,
  } = useProjectContext();

  // Auto-refresh when returning to the Transformations list (e.g. from a batch workspace).
  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  const batches: BatchSummary[] = useMemo(
    () =>
      (projectSummary?.batches ?? []).map((batch) => ({
        ...batch,
        status: normalizeStatusForUi(batch.status),
      })),
    [projectSummary?.batches]
  );

  // Filter out scheduled batches — they live in the Schedule tab's run history.
  // Scheduled batches use client_id 'scheduler' or 'scheduler-<suffix>' for sub-batches.
  const interactiveBatches = useMemo(
    () => batches.filter(b => !b.client_id.startsWith('scheduler')),
    [batches]
  );

  // Derive schedule summary from any batch that has schedule info
  const scheduleSummary = useMemo(() => {
    const withSchedule = batches.find(b => b.total_scheduled_runs && b.total_scheduled_runs > 0);
    if (!withSchedule) return null;
    return {
      name: withSchedule.schedule_name ?? 'Schedule',
      totalRuns: withSchedule.total_scheduled_runs ?? 0,
      lastRunStatus: withSchedule.last_run_status,
      lastRunAt: withSchedule.last_run_at,
    };
  }, [batches]);

  const uploadDisabled = !selectedProjectId;

  const handleViewBatch = (batchId: string) => {
    navigate(`/batches/${batchId}`);
  };

  const handleStartRename = (e: React.MouseEvent, batchId: string, currentName: string) => {
    e.stopPropagation();
    setEditingBatchId(batchId);
    setEditingName(currentName);
  };

  const handleSaveRename = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editingBatchId || !editingName.trim()) return;
    try {
      await renameBatch(editingBatchId, editingName.trim());
      setEditingBatchId(null);
      refreshSummary();
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  const handleCancelRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingBatchId(null);
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setBlinkKey((k) => k + 1);

    try {
      await refreshSummary();
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 450);
    }
  };

  const handleUploadFlowComplete = (result: UploadCompletionResult) => {
    setUploadDialogOpen(false);
    refreshSummary();
    switch (result.action) {
      case 'cancel':
        // User cancelled — batch was deleted, stay on list
        break;
      case 'review_first':
        navigate(`/batches/${result.batchId}/workspace`);
        break;
      case 'regenerate_delta':
      case 'regenerate_full':
        navigate(`/batches/${result.batchId}/workspace`);
        break;
      default:
        navigate(`/batches/${result.batchId}`);
        break;
    }
  };

  const handleSchemaSetSelect = async (selected: Schema) => {
    setSchemaPickerOpen(false);
    if (selectedProjectId) {
      await updateProject(selectedProjectId, { targetSchemaId: selected.schema_id });
      refreshSummary();
    }
  };

  if (isLoadingSummary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      key={blinkKey}
      sx={{
        ...(isRefreshing ? blinkAnimation : {}),
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Transformations
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={
              isRefreshing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Refresh />
              )
            }
            onClick={handleRefresh}
            disabled={isLoadingSummary || isRefreshing}
            size="small"
            sx={{ mr: 2 }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
            disabled={uploadDisabled}
          >
            New Transformation
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {selectedProjectId ? (
            <>
              <Typography variant="h6" gutterBottom>
                Project Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary">
                        Active Project
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {projectSummary?.project?.name ?? selectedProjectId}
                      </Typography>
                      <Box mt={0.5}>
                        <GuidDisplay guid={selectedProjectId} abbreviate />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary">
                        Transformations
                      </Typography>
                      <Typography variant="h5">
                        {interactiveBatches.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  {scheduleSummary ? (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="textSecondary">
                          Active Schedule
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {scheduleSummary.name}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                          <Chip
                            label={`${scheduleSummary.totalRuns} runs`}
                            size="small"
                            icon={<Schedule />}
                            variant="outlined"
                          />
                          {scheduleSummary.lastRunStatus && (
                            <Chip
                              label={scheduleSummary.lastRunStatus}
                              size="small"
                              color={scheduleSummary.lastRunStatus === 'completed' ? 'success' : scheduleSummary.lastRunStatus === 'failed' ? 'error' : 'default'}
                            />
                          )}
                        </Stack>
                        {scheduleSummary.lastRunAt && (
                          <Typography variant="caption" color="text.secondary">
                            Last run {formatRelativeTime(scheduleSummary.lastRunAt)}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="textSecondary">
                          Format Drafts
                        </Typography>
                        <Typography variant="h5">
                          {projectSummary?.pending_schemas?.length ?? 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>

              </Grid>
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Business sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Project Selected
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                Select a project from the top bar to view its transformations.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {(!interactiveBatches || interactiveBatches.length === 0) ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No transformations yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload your data files to get started with AI-powered transformation.
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
              disabled={uploadDisabled}
            >
              New Transformation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Target Format</TableCell>
                <TableCell align="right">
                  <Tooltip title="Output rows (successfully exported to target) / Source rows (analyzed)" arrow>
                    <Typography variant="subtitle2" component="span" sx={{ cursor: 'help' }}>
                      Output Rows / Source Rows
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {interactiveBatches.map((batch) => {
                const projectId = batch.project_id ?? selectedProjectId ?? '-';
                const projectName = projectSummary?.project?.name ?? projectId;
                const statusCfg = getStatusConfig(batch.status);
                const hasScheduledRuns = (batch.total_scheduled_runs ?? 0) > 0;
                return (
                  <TableRow key={batch.batch_id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewBatch(batch.batch_id)}>
                    <TableCell>
                      {editingBatchId === batch.batch_id ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                          <TextField
                            value={editingName}
                            onChange={(e) => {
                              if (e.target.value.length <= 200) setEditingName(e.target.value);
                            }}
                            size="small"
                            autoFocus
                            error={editingName.trim().length === 0}
                            helperText={editingName.trim().length === 0 ? 'Name cannot be empty' : `${editingName.length}/200`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && editingName.trim()) handleSaveRename(e as any);
                              if (e.key === 'Escape') { e.stopPropagation(); setEditingBatchId(null); }
                            }}
                            inputProps={{ maxLength: 200 }}
                            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.5, px: 1 }, maxWidth: 220 }}
                          />
                          <IconButton size="small" onClick={handleSaveRename} color="success" disabled={!editingName.trim()}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                          <IconButton size="small" onClick={handleCancelRename}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title={batch.batch_id}>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 280 }}>
                              {batch.batch_name || batch.batch_id.slice(0, 8)}
                            </Typography>
                          </Tooltip>
                          <Tooltip title="Rename">
                            <IconButton size="small" onClick={(e) => handleStartRename(e, batch.batch_id, batch.batch_name || batch.batch_id.slice(0, 8))} sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                      {hasScheduledRuns && (
                        <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
                          <Chip
                            icon={<Schedule />}
                            label={`${batch.total_scheduled_runs} scheduled runs`}
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/batches/${batch.batch_id}/schedule`);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                          {batch.last_run_status && (
                            <Typography variant="caption" color="text.secondary">
                              Last: {batch.last_run_status}{batch.last_run_at ? ` ${formatRelativeTime(batch.last_run_at)}` : ''}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{projectName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusCfg.label}
                        color={statusCfg.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {(statusCfg.step >= 3 || batch.status === 'mapping') ? (
                        batch.target_schema_id ? (
                          targetSchema && batch.target_schema_id === targetSchema.schema_id ? (
                            <Chip
                              label={targetSchema.schema_name}
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={(e) => { e.stopPropagation(); setPreviewSchema(targetSchema); }}
                              sx={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <GuidDisplay guid={batch.target_schema_id} abbreviate />
                          )
                        ) : (
                          <Tooltip title="Click to assign a target schema">
                            <Chip
                              label="Not set"
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={(e) => { e.stopPropagation(); setSchemaPickerOpen(true); }}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )
                      ) : (
                        <Tooltip title="Target format will be determined after analysis is complete">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', opacity: 0.3, pl: 1 }}>
                            <Schedule sx={{ fontSize: 18 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={!batch.processed_rows ? "Export data to see processed counts." : ""}
                        arrow
                      >
                        <Typography
                          variant="body2"
                          color={(!batch.processed_rows && !batch.total_rows) ? "text.secondary" : "text.primary"}
                        >
                          {(!batch.processed_rows && !batch.total_rows)
                            ? "—"
                            : (!batch.processed_rows)
                              ? `— / ${(batch.total_rows ?? 0).toLocaleString()}`
                              : `${(batch.processed_rows ?? 0).toLocaleString()} / ${(batch.total_rows ?? 0).toLocaleString()}`}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(batch.created_at ?? undefined)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(batch.created_at ?? undefined, 'HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); handleViewBatch(batch.batch_id); }}
                        size="small"
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Fab
        color="primary"
        aria-label="New Transformation"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setUploadDialogOpen(true)}
        disabled={uploadDisabled}
      >
        <Add />
      </Fab>

      {/* Schema Preview Dialog */}
      <SchemaPreviewDialog
        open={Boolean(previewSchema)}
        schema={previewSchema}
        onClose={() => setPreviewSchema(null)}
      />

      {/* Schema Set Picker Dialog — assign target format to project */}
      <SchemaSetPickerDialog
        open={schemaPickerOpen}
        onClose={() => setSchemaPickerOpen(false)}
        onSelect={handleSchemaSetSelect}
        currentSchemaId={targetSchema?.schema_id}
      />

      {/* Smart Upload Dialog — handles upload + recipe compatibility check */}
      <SmartUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onComplete={handleUploadFlowComplete}
        projectId={selectedProjectId}
        schemaSet={targetSchema}
        title="New Transformation"
        defaultSourceConnectorId={projectSummary?.project?.source_connector_id}
      />
    </Box>
  );
};

export default BatchList;
